import React, { useState, useRef } from "react";
import "./contentpage.css";
import FloatingPageNav from "../components/FloatingPageNav";
import LandingSection from "./LandingSection";
import Question from "../components/Question";


function IslamicTradePage14() {
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-13", 
    index: "/index",                 
    next: "/islamic-trade-page-15", 
  };
  const titleText = "Rulings on Al-Hawala (Transference of Debt)";
  const questions = [
    {
      text: "1. What is the primary objective of Al Hawala in Islamic finance??",
      options: [
        {
          value: "A",
          label: "A. To avoid paying debts altogetherd.",
        },
        {
          value: "B",
          label:
            "B. To transfer financial responsibility to another capable party.",
        },
        {
          value: "C",
          label: "C. To convert debt into a business investment.",
        },
        {
          value: "D",
          label: "D. To delay repayment of a loan indefinitely.",
        },
      ],
      correct: "B",
    },
    {
      text: "2. According to Islamic rulings, when is the transfer of debt through Hawala considered invalid??",
      options: [
        { value: "A", label: "A.  When the new debtor is a close relative." },
        { value: "B", label: "B. When the original debtor is poor." },
        {
          value: "C",
          label:
            "C. When the transferred debt lacks a clear and established source of repayment.",
        },
        { value: "D", label: "D. When the original debt is in gold." },
      ],
      correct: "C",
    },
    {
      text: "3. If a debt of 100kg of rice is transferred via Hawala, what must the new debtor pay??",
      options: [
        { value: "A", label: "A.  100kg of barley." },
        { value: "B", label: "B. The equivalent cash value of the rice." },
        {
          value: "C",
          label: "C. 100kg of rice, unless otherwise agreed by the lender.",
        },
        { value: "D", label: "D. Any commodity they prefer." },
      ],
      correct: "C",
    },
    {
      text: "4. What is the guidance of Prophet Muhammad (peace be upon him) about accepting debt transfer to a solvent person?",
      options: [
        { value: "A", label: "A. Reject the transfer if it seems unfair." },
        {
          value: "B",
          label:
            "B. If your debt is transferred to a wealthy person, accept the transfer.",
        },
        { value: "C", label: "C. Debt cannot be transferred in Islam." },
        {
          value: "D",
          label: "D. The lender must always approve the transfer.",
        },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={14}
        timerMinutes={60}
        currentLevel={1}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 14</b>! Today, we delve into the concept of Al-Hawala,
        a powerful tool in Islamic finance that allows the transference of debt
        from one individual to another. This practice, endorsed by the Prophet
        Muhammad (peace be upon him), reflects the Shariah’s emphasis on
        flexibility, trust, and justice in financial transactions. As we explore
        the rules and conditions surrounding Hawala, you’ll see how Islam
        provides practical solutions to reduce hardship, resolve debts
        efficiently, and maintain the integrity of financial relationships
        within society.
        <br />
        <br /> <b>The Concept and Spirit of Al-Hawala in Islamic Finance</b>
        <br />
        <br />
        The term Hawala (الحوالة) literally means “transfer” or “assignment.” In
        the context of Islamic jurisprudence (fiqh al-mu‘amalat), it refers to
        the transfer of a debt or financial obligation from one debtor to
        another. This allows a person who owes a debt to shift that liability to
        a third party who agrees to take it on. The third party thus becomes
        directly responsible to the creditor, while the original debtor is
        released from liability.
        <br />
        This mechanism is rooted deeply in the values of trust (amanah) and
        mutual cooperation (ta‘awun) that form the foundation of Islamic
        economic life. The Qur’an encourages believers to assist one another in
        righteousness and piety, not in sin and injustice:
        <br />
        "Cooperate with one another in goodness and righteousness, and do not
        cooperate in sin and transgression. And be mindful of Allah. Surely
        Allah is severe in punishment."
        <a href="https://quran.com/5/2" target="blanj">
          (Surah Al Ma'idah 2)
        </a>
        <br />
        In this spirit, Al-Hawala serves as a legitimate, ethical means for
        easing the financial burdens of others while maintaining justice among
        all parties involved. It is a testament to Islam’s recognition that
        financial obligations can sometimes become difficult, and thus a
        flexible yet principled solution must exist to ensure economic stability
        and moral fairness.
        <br />
        The Prophet Muhammad (peace be upon him) endorsed the practice of Hawala
        through his clear and timeless guidance:
        <br />
        "Procrastination (delay) in paying debts by a wealthy man is injustice.
        So, if your debt is transferred from your debtor to a rich debtor, you
        should agree."
        <a href="https://sunnah.com/bukhari:2287" target="blank">
          (Sahih Al Bukhari 2287)
        </a>
        <br />
        This hadith lays down the ethical framework of Hawala: if a debtor
        arranges for a capable person to assume the debt, the creditor should
        accept the arrangement without objection. It encourages both financial
        responsibility and social consideration, which are values that lie at
        the heart of Islamic economics.
        <br />
        Historically, Hawala emerged as an instrument of financial cooperation
        and risk sharing in Muslim societies, particularly during trade across
        vast distances. Merchants would transfer debt obligations through
        intermediaries to avoid carrying large sums of money while traveling.
        This practice laid the groundwork for later financial instruments such
        as bills of exchange and letters of credit, centuries before similar
        systems developed in Europe. Islamic civilization thus pioneered
        structured credit and debt-transfer mechanisms, rooted in ethical
        accountability.
        <br />
        At its core, Hawala embodies three important Islamic objectives
        (maqasid):
        <ul>
          <li>Ease (taysir) – to remove hardship from debtors.</li>
          <li>Justice (‘adl) – to ensure creditors’ rights are protected.</li>
          <li>
            Social solidarity (ukhuwwah) – to strengthen community trust through
            financial cooperation.
          </li>
        </ul>
        In the following sections, we will explore the essential conditions,
        mechanisms, and implications of Hawala in detail, showing how it remains
        not only a spiritual principle but also a practical system relevant to
        both classical and modern financial contexts.
        <br />
        <br />
        <b>Legal Foundations and Conditions for a Valid Hawala</b>
        <br />
        <br />
        To understand how Hawala functions in Islamic law, one must examine its
        juridical framework within the understanding of the early Muslims
        (Salaf). The concept is unanimously accepted by Islamic Scholars.
        <br />
        At its essence, a valid Hawala contract involves three parties:
        <ul>
          <li>The debtor (muhil) — who transfers the debt.</li>
          <li>The creditor (muhal lahu) — to whom the debt is owed.</li>
          <li>
            The transferee (muhal ‘alayhi) — the person who accepts
            responsibility for the debt.
          </li>
        </ul>
        The classical scholars agree that once a valid Hawala occurs, the debt
        is transferred entirely, meaning the creditor must now deal directly
        with the new debtor, and the original debtor is discharged of liability.
        This ruling ensures clarity and prevents disputes.
        <br />
        Condition 1: Existence of a Valid and Established Debt
        <br />
        The first essential condition for Hawala is that the debt being
        transferred must already exist and be confirmed. It cannot be
        hypothetical or dependent on uncertain events. Islamic law prohibits
        ambiguity (gharar) in contracts, and hence a debt that is uncertain,
        disputed, or not yet due cannot be validly transferred.
        <br />
        For example, if Person A owes 10,000 rupees to Person B, and Person C
        owes 10,000 rupees to A, then A may transfer his debt to C, meaning C
        now becomes directly responsible to pay B. However, if the debt between
        A and C is uncertain (e.g., based on an unconfirmed sale or pending
        condition), the Hawala would be invalid. This ensures transparency and
        eliminates financial deception.
        <br />
        Condition 2: Transferability Requires an Established Right
        <br />
     Islamic jurists stipulate that Hawala can only be
        executed when the transferee’s ability to fulfill the obligation is
        based on a definite and irrevocable source of funds. In other words, the
        new debtor must have a clearly established financial liability towards
        the original debtor.
        <br />
        A common example is when a landlord (Person A) is owed rent by a tenant
        (Person B), while Person A himself owes money to a supplier (Person C).
        Person A can legally transfer his debt to C by directing that Person B
        (the tenant) pay C directly. Since the rent obligation is established
        and due, this transfer is valid.
        <br />
        However, transferring a debt to a newly married woman who has not yet
        received her Mahr (dowry) would not be valid, as the Mahr is contingent
        upon the consummation of the marriage. If the marriage is dissolved
        before consummation, the woman may not receive the full Mahr. In such a
        case, the source of repayment is uncertain and not firmly established,
        making the transfer invalid according to Islamic principles.
        <br />
        Condition 3: Adherence to the Original Debt’s Specie and Terms
        <br />
        Islamic law demands that the transferred debt must preserve the original
        terms and nature (specie) of the obligation. This prevents unfair
        modification or substitution under the guise of a transfer. For example,
        if Person A owes 100 kilograms of rice to a lender, and transfers the
        debt to Person B, Person B must also repay in rice, not in wheat or
        barley. Changing the repayment substance would amount to a new sale
        contract, not a transfer of liability.
        <br />
        That said, Islam allows flexibility through mutual consent. If the
        creditor voluntarily agrees to extend the repayment period or accept a
        slightly different form of repayment, such modifications are valid. This
        balance between rigidity and mercy reflects the Shariah’s aim of
        combining justice with compassion.
        <br />
        Condition 4: The Lender’s Consent — A Matter of Juristic Discussion
        <br />
        In the context of Hawala, the lender's consent is not a requirement for
        the transfer of debt. Once a debtor transfers their financial obligation
        to another capable individual, the lender is obligated to accept the new
        debtor as the responsible party. This principle underscores the
        practicality of Hawala, ensuring that financial obligations can be
        managed efficiently without undue complications from seeking lender
        approval.
        <br />
        <br />
        <b>Ethical and Economic Dimensions of Hawala</b>
        <br />
        <br />
        Beyond its legal framework, Al-Hawala carries deep ethical and
        socio-economic implications. It is a practice rooted in the Prophetic
        vision of a compassionate society that safeguards the dignity of debtors
        while preserving the rights of creditors. In the pre-Islamic era
        (Jahiliyyah), debt relations were often exploitative. Creditors would
        charge excessive interest and impose harsh penalties on those unable to
        pay. Islam revolutionized this system by introducing a moral economy
        grounded in fairness, empathy, and social solidarity. Hawala was one
        such reform, transforming debt management from a tool of oppression into
        a means of mercy.
        <br />
        A Mechanism of Social Ease <br />
        By permitting the transfer of debts, Hawala helps reduce the financial
        pressure on individuals. For instance, if a person falls into hardship,
        they may find someone else in the community willing to take
        responsibility for the debt. This system encourages Muslims to assist
        one another financially, fulfilling the Qur’anic directive to “Cooperate
        with one another in goodness and righteousness”
        <a href="https://quran.com/5/2" target="blank">
          (Surah Al Ma'idah 2)
        </a>
        <br />
        Furthermore, the Prophet (peace be upon him) encouraged helping debtors
        and warned of the moral consequences of neglecting financial
        obligations:
        <br /> "The believer's soul is suspended by his debt until it is settled
        for him."
        <a href="https://sunnah.com/tirmidhi:1078" target="blank">
          (Jami At-Tirmidhi 1078)
        </a>
        <br />
        Trust and Moral Accountability
        <br />
        At the heart of Hawala lies trust (thiqa). All parties must act with
        sincerity and transparency, ensuring that no deception, coercion, or
        exploitation takes place. The debtor must be honest about their
        liability, the transferee must accept responsibility willingly, and the
        creditor must not act unjustly in demanding repayment.
        <br />
        The Prophet (peace be upon him) condemned financial dishonesty, stating:
        <br />
        "The borrowed is to be returned, and the guarantor is responsible, and
        the debt is to be repaid." (Jami At Tirmidhi 1265)
        <br />
        Economic Function of Hawala in Trade: <br />
        From an economic perspective, Hawala facilitates fluidity of capital and
        efficiency in trade. It reduces the need for cash transactions,
        particularly over long distances, and allows debts to circulate safely
        within trusted networks. This system became especially vital during the
        Golden Age of Islamic trade, connecting merchants from Andalusia to
        India through trust-based transfer mechanisms that predated modern
        banking.
        <br />
        <br />
        <b>Practical Applications and Contemporary Relevance</b>
        <br />
        <br />
        As we conclude our exploration, it is crucial to see how the principle
        of Hawala continues to shape financial ethics and practices today. While
        rooted in classical jurisprudence, its values resonate strongly in
        modern times where global trade, remittances, and financial
        intermediation are central to daily life.
        <br />
        Modern-Day Hawala and Money Transfers:
        <br />
        In contemporary finance, the term “Hawala” is often used to describe
        informal money transfer systems popular across South Asia, the Middle
        East, and Africa. While these systems resemble the classical concept,
        there are key differences. The traditional Hawala in Islamic law is a
        transfer of debt, whereas modern “Hawala networks” are remittance
        systems that rely on trust among brokers.
        <br />
    
        Ethical Guidelines in Practice: For Hawala to remain Shariah-compliant,
        three conditions must always be met,
        <ul>
          <li>The transfer must not conceal riba (interest).</li>
          <li>The debt being transferred must be real, not fictitious.</li>
          <li>
            All transactions must be recorded clearly to prevent fraud or
            confusion.
          </li>
        </ul>
        In addition, Islamic jurists emphasize intention (niyyah), the moral
        underpinning of any contract. A person transferring a debt should do so
        out of sincerity and fairness, not as a means to escape legitimate
        responsibility. <br />
        Spiritual and Social Significance:
        <br />
        Finally, Hawala serves as a reminder of communal responsibility in
        Islam. The system is not merely financial but deeply spiritual. It
        reinforces the Prophetic message that Muslims are guardians of one
        another’s welfare. The willingness of one believer to assume another’s
        burden exemplifies ihsan, which the highest level of moral excellence.
        <br />
        The hadith narrated by Abu Qatadah beautifully illustrates this ethos.
        When a man died leaving unpaid debts, the Prophet (peace be upon him)
        initially refrained from leading his funeral prayer until Abu Qatadah
        said, <br />
        “I shall pay them, Messenger of Allah.”
        <a href="https://sunnah.com/abudawud:3343" target="blank">
          (Sunan Abu Dawood, Hadith 3343)
        </a>
        The Prophet then performed the prayer. This story shows that
        guaranteeing and settling another’s debt is not merely a financial act
        but an act of worship that elevates one’s spiritual standing.
        <br />
        <br />
        Al-Hawala stands as one of the most elegant illustrations of Islam’s
        balance between moral principles and economic pragmatism. It transforms
        the act of settling debts into a collective responsibility guided by
        faith, trust, and compassion. By allowing debt transfer under ethical
        conditions, Islam protects both creditor and debtor, ensuring justice
        without oppression.
        <br />
        In a world where financial systems often prioritize profit over people,
        revisiting principles like Hawala reminds us that economic activity can,
        and should reflect moral integrity. It invites believers to participate
        in an economy of care, where fulfilling obligations is both a duty to
        society and an act of devotion to Allah.
        <br />
        As you complete today’s lesson, remember that every Islamic financial
        rule, whether concerning Hawala, Dhaman, or Kafalah, is not merely about
        contracts and transactions. It is about cultivating a community of
        honesty, cooperation, and mutual support. Through these timeless
        principles, Islam continues to offer a moral compass for an ethical and
        compassionate financial world.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In Today's lesson we learned the Islamic concept of debt transference.
        It explained how a debtor can transfer their obligation to a third party
        who then becomes responsible for repayment. The lesson emphasized key
        conditions for a valid transfer such as the new debtor’s financial
        capability and the preservation of the original debt’s terms. We also
        learned that the lender’s consent is not required once the transfer is
        made to a solvent individual, as supported by the Prophet’s (peace be
        upon him) guidance. Al Hawala reflects Islam’s commitment to fairness,
        flexibility, and mutual trust in financial dealings.
        <br/><br/>
              <b>Coming up Next</b><br/><br/>
      In the next session, we will lay the essential groundwork that leads naturally into the concept of Sulh. Day 15 focuses on the Islamic approach to disputes, claims, and denials i.e how rights are established, how truth is determined, and the ethical responsibilities of both claimants and defendants. By exploring the principles of justice, evidence, acknowledgment, and accountability in disagreements, the next lesson prepares you to understand why reconciliation in Islam must always be rooted in truth. <br/><br/>
      </p>

      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
   <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
       
    </div>
  );
}

export default IslamicTradePage14;
